var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @desc gauge chart
 */
import { createRef } from '@byted/light-render';
import { interpolateColor } from '@byted/light-render/lib/interpolate';
import * as lodash from '../../utils/lodash';
import { Group, Line, Sector, Circle, ConicGradient } from '../../element';
import { createSymbol } from '../../symbol';
import { SeriesModel } from '../../model';
import { isPercent, getPercentOrNumberValue, degToRad, getPolar, interpolateNumber, } from '../../utils/math';
import LinearScale from '../../scale/linear';
import { registerChart } from '../index';
import { getCircleLayoutLabelAlign } from '../../utils/textLayout';
import { createRichText } from '../../rich';
import { isString } from '../../utils/lodash';
/**
 * gauge export
 */
var GaugeModel = /** @class */ (function (_super) {
    __extends(GaugeModel, _super);
    function GaugeModel() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'gauge';
        _this._pointerRef = createRef();
        _this._progressSectorRef = createRef();
        _this._labelRef = createRef();
        return _this;
    }
    GaugeModel.prototype.getDefaultOption = function () {
        return {
            type: 'gauge',
            name: '',
            center: ['50%', '50%'],
            startAngle: -210,
            endAngle: 30,
            size: '75%',
            min: 0,
            max: 100,
            value: 0,
            range: [0, 20, 80, 100],
            // colors: null, // red-blue
            background: 'rgba(0, 0, 0, 0)',
            useProgressMode: false,
            splitProgress: false,
            bandStyle: {
                width: '7.5%',
                background: '#EFF1F9',
                round: false,
            },
            pointer: {
                show: true,
                length: '80%',
                width: 8,
                fill: '#4D5369',
                icon: 'gaugePointer',
                offsetX: 0,
                offsetY: 0,
            },
            encode: null,
            label: {
                show: true,
                countUp: false,
                position: null,
                distance: 0,
                offsetX: 0,
                offsetY: '20%',
                color: '#4D5369',
                fontSize: 20,
                fontWeight: 'bold',
                textAlign: 'center',
                textBaseline: 'top',
            },
            tickLine: {
                show: true,
                interval: 25,
                length: 8,
                inside: true,
                margin: 12,
                lineWidth: 2,
                stroke: '#878EA8',
                lineCap: 'round',
            },
            subtick: {
                show: true,
                tickCount: 4,
                length: 5,
                lineWidth: 2,
                stroke: '#878EA8',
                lineCap: 'round',
            },
            tickLabel: {
                show: true,
                margin: 24,
                inside: true,
                fontSize: 12,
                color: '#333333',
                formatter: null,
            },
            title: {
                show: true,
                offsetX: 0,
                offsetY: '-40%',
                text: '',
                color: '#6E748E',
                fontSize: 14,
                textAlign: 'center',
                textBaseline: 'bottom',
            },
            showLegend: false,
            visible: true,
            animate: {
                during: 1000,
            },
            gradientMode: false,
        };
    };
    GaugeModel.prototype.getInitialState = function () {
        return {
            cx: null,
            cy: null,
            value: null,
            radius: null,
            innerRadius: null,
            startAngle: null,
            endAngle: null,
            angle: null,
            percent: null,
            needAnimation: this.needAnimation(),
            points: [],
            bandItems: [],
            ticks: [],
        };
    };
    GaugeModel.prototype.beforeRender = function () {
        var _this = this;
        var defaultColors = this.chart.getOption().colors;
        var _a = this.option, center = _a.center, startAngle = _a.startAngle, guageEndAngle = _a.endAngle, min = _a.min, max = _a.max, value = _a.value, range = _a.range, _b = _a.colors, colors = _b === void 0 ? defaultColors : _b, tickLine = _a.tickLine, bandStyle = _a.bandStyle;
        var endAngle = lodash.isNumber(guageEndAngle) ? guageEndAngle : startAngle + 360;
        var _c = this.getFacetBBox(), top = _c.top, left = _c.left, width = _c.width, height = _c.height;
        var radius = getPercentOrNumberValue(this.option.size, Math.min(width, height)) / 2;
        var innerRadius = radius - getPercentOrNumberValue(bandStyle.width, radius * 2);
        var cx = (isPercent(center[0]) ? left + getPercentOrNumberValue(center[0], width) : center[0]);
        var cy = (isPercent(center[1]) ? top + getPercentOrNumberValue(center[1], height) : center[1]);
        var rangeData = [];
        var guageScale = new LinearScale(min, max, startAngle, endAngle);
        this._scale = guageScale;
        if (range.length > 0) {
            // be careful, currentIndex start from 1
            range.reduce(function (prev, current, currentIndex) {
                var color = _this.getSeriesColor();
                if (lodash.isArray(colors)) {
                    color = colors[(currentIndex - 1) % colors.length];
                }
                else if (lodash.isString(colors)) {
                    var _a = colors.split('-'), startColor = _a[0], endColor = _a[1];
                    var progress = currentIndex / (range.length - 1);
                    color = interpolateColor(startColor, endColor, progress);
                }
                var data = {
                    start: prev,
                    end: current,
                    startAngle: guageScale.scale(prev),
                    endAngle: guageScale.scale(current),
                    color: color,
                };
                rangeData.push(data);
                return current;
            });
        }
        var ticks = [];
        var tickCount = Math.ceil((max - min) / tickLine.interval);
        var percent = (value - min) / (max - min);
        var angle = guageScale.scale(value);
        for (var i = 0; i <= tickCount; i++) {
            ticks.push(Math.min(min + i * tickLine.interval, max));
        }
        var state = {
            cx: cx,
            cy: cy,
            value: value,
            radius: radius,
            innerRadius: innerRadius,
            startAngle: startAngle,
            endAngle: endAngle,
            angle: angle,
            percent: percent,
            needAnimation: this.state.needAnimation,
            points: [
                {
                    name: this.option.name,
                    value: value,
                    angle: angle,
                    visible: true,
                    color: null,
                    encode: null,
                    percent: Number((percent * 100).toFixed(2)) + "%",
                    band: null,
                },
            ],
            bandItems: rangeData,
            ticks: ticks,
        };
        this.setState(state);
    };
    GaugeModel.prototype.afterRender = function () {
        var _this = this;
        var _a = this.option, gradientMode = _a.gradientMode, useProgressMode = _a.useProgressMode, max = _a.max, min = _a.min;
        var needAnimation = this.state.needAnimation;
        if (needAnimation) {
            var _b = this.getAnimateOption().during, during = _b === void 0 ? 1000 : _b;
            this._pointerRef.current.animateTo({
                rotation: this._getPointerRotation(this.state.points[0].angle),
            }, {
                during: during,
                onFrame: function (progress) {
                    if (!_this._labelRef.current || !_this.option.label.countUp) {
                        return;
                    }
                    var tickValue = interpolateNumber(progress, [0, 1], [_this.option.min, _this.option.value]);
                    var value = _this.state.points[0].value;
                    _this.state.points[0].value = tickValue;
                    var label = _this.getDataLabel(0);
                    _this.state.points[0].value = value;
                    if (!_this.option.label.rich) {
                        _this._labelRef.current.setAttr({ text: label });
                    }
                },
            });
            if (this._progressSectorRef.current) {
                var value = lodash.clamp(this.state.value, min, max);
                var endAngle = useProgressMode ? this._scale.scale(value) : this._scale.scale(max);
                var color = this.getValueColor(value);
                var toAttr = gradientMode
                    ? { end: degToRad(endAngle) }
                    : { fill: color, end: degToRad(endAngle) };
                this._progressSectorRef.current.animateTo(toAttr, during);
            }
            this.setState({ needAnimation: false });
        }
    };
    GaugeModel.prototype.getValueColor = function (value) {
        var _this = this;
        var bandStyle = this.option.bandStyle;
        var color = bandStyle.background;
        this.state.bandItems.forEach(function (range, index) {
            var start = range.start, end = range.end;
            var isLast = index === _this.state.bandItems.length - 1;
            var biggerThanStart = index === 0 ? value >= start : value > start;
            var smallerThanEnd = isLast ? value < end : value <= end;
            if (biggerThanStart && smallerThanEnd) {
                color = range.color;
            }
            if (index === 0 && value < start) {
                color = range.color;
            }
            if (isLast && value >= end) {
                color = range.color;
            }
        });
        return color;
    };
    GaugeModel.prototype.renderPointer = function () {
        var _a = this.option.pointer, show = _a.show, width = _a.width, length = _a.length, fill = _a.fill, icon = _a.icon, offsetX = _a.offsetX, offsetY = _a.offsetY;
        var _b = this.state, cx = _b.cx, cy = _b.cy, radius = _b.radius, innerRadius = _b.innerRadius, startAngle = _b.startAngle, points = _b.points, needAnimation = _b.needAnimation;
        var angle = points[0].angle;
        var pointerLength = getPercentOrNumberValue(length, innerRadius);
        var color = this.getValueColor(this.state.value);
        var x = cx - width / 2 + getPercentOrNumberValue(offsetX, radius);
        var y = cy - pointerLength + getPercentOrNumberValue(offsetY, radius);
        var pointer = createSymbol({
            symbol: icon,
            stroke: null,
            lineWidth: 0,
        }, {
            x: x,
            y: y,
            width: width,
            height: pointerLength,
        }, {
            ref: this._pointerRef,
            key: 'gauge-pointer',
            display: show,
            fill: fill || color,
            origin: [cx, cy],
            rotation: this._getPointerRotation(needAnimation ? startAngle : angle),
        });
        return pointer;
    };
    GaugeModel.prototype.renderBands = function () {
        var _this = this;
        var _a = this.option, optionValue = _a.value, min = _a.min, max = _a.max, useProgressMode = _a.useProgressMode, splitProgress = _a.splitProgress, gradientMode = _a.gradientMode;
        var _b = this.state, cx = _b.cx, cy = _b.cy, radius = _b.radius, innerRadius = _b.innerRadius, needAnimation = _b.needAnimation;
        var group = new Group({
            key: this.getKey('gauge-range'),
        });
        var value = lodash.clamp(optionValue, min, max);
        if (gradientMode || (useProgressMode && !splitProgress)) {
            var gradientColor = gradientMode ? this._getGradientColor() : null;
            var startColor = gradientColor || this.getValueColor(min);
            var color = gradientColor || this.getValueColor(value);
            var rangeStartAngle = this.state.startAngle;
            var rangeEndAngle = useProgressMode ? this._scale.scale(value) : this.state.endAngle;
            var progressSector = new Sector({
                ref: this._progressSectorRef,
                key: 'gauge-progress',
                cx: cx,
                cy: cy,
                radius: radius,
                radiusI: innerRadius,
                start: degToRad(rangeStartAngle),
                end: degToRad(needAnimation ? rangeStartAngle : rangeEndAngle),
                fill: needAnimation ? startColor : color,
                round: this.option.bandStyle.round,
            });
            group.add(progressSector);
        }
        else {
            group.addAll(this.state.bandItems.map(function (range) {
                var startAngle = range.startAngle, endAngle = range.endAngle, color = range.color, start = range.start, end = range.end;
                var rangeEndAngle = endAngle;
                if (_this.option.useProgressMode) {
                    if (lodash.inRange(value, start, end)) {
                        rangeEndAngle = _this._scale.scale(value);
                    }
                    else if (start >= value) {
                        return null;
                    }
                }
                return new Sector({
                    cx: cx,
                    cy: cy,
                    radius: radius,
                    radiusI: innerRadius,
                    start: degToRad(startAngle),
                    end: degToRad(rangeEndAngle),
                    fill: color,
                    round: _this.option.bandStyle.round,
                });
            }));
        }
        return group;
    };
    GaugeModel.prototype.renderBandBackground = function () {
        var _a = this.state, cx = _a.cx, cy = _a.cy, startAngle = _a.startAngle, endAngle = _a.endAngle, radius = _a.radius, innerRadius = _a.innerRadius;
        return new Sector({
            key: 'band-background',
            cx: cx,
            cy: cy,
            start: degToRad(startAngle),
            end: degToRad(endAngle),
            radius: radius,
            radiusI: innerRadius,
            fill: this.option.bandStyle.background,
            round: this.option.bandStyle.round,
        });
    };
    GaugeModel.prototype.renderTickLine = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('guage-ticks'),
        });
        var tickLine = this.option.tickLine;
        var _a = this.state, cx = _a.cx, cy = _a.cy, radius = _a.radius, innerRadius = _a.innerRadius, startAngle = _a.startAngle, endAngle = _a.endAngle, ticks = _a.ticks;
        var scale = this._scale;
        var polar = getPolar({ cx: cx, cy: cy });
        var r = tickLine.inside ? innerRadius : radius;
        ticks.forEach(function (tick, index) {
            if (index === 0 && endAngle - startAngle >= 360) {
                return;
            }
            var tickAngle = scale.scale(tick);
            var p1 = polar(tickAngle, r + tickLine.margin * (tickLine.inside ? -1 : 1));
            var p2 = polar(tickAngle, r + (tickLine.length + tickLine.margin) * (tickLine.inside ? -1 : 1));
            group.add(new Line(__assign({ x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, stroke: _this.getValueColor(tick) }, _this.getLineStyle(_this.option.tickLine))));
        });
        return group;
    };
    GaugeModel.prototype.renderSubticks = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('sub-guage-ticks'),
        });
        var _a = this.option, tickLine = _a.tickLine, subtick = _a.subtick;
        var _b = this.state, cx = _b.cx, cy = _b.cy, radius = _b.radius, innerRadius = _b.innerRadius, ticks = _b.ticks;
        var scale = this._scale;
        var polar = getPolar({ cx: cx, cy: cy });
        var subInterval = tickLine.interval / (subtick.tickCount + 1);
        var r = tickLine.inside ? innerRadius : radius;
        ticks.forEach(function (tick, index) {
            if (index === ticks.length - 1) {
                return;
            }
            for (var i = 0; i < subtick.tickCount; i++) {
                var subValue = tick + (i + 1) * subInterval;
                var tickAngle = scale.scale(subValue);
                var p1 = polar(tickAngle, r + tickLine.margin * (tickLine.inside ? -1 : 1));
                var p2 = polar(tickAngle, r + (subtick.length + tickLine.margin) * (tickLine.inside ? -1 : 1));
                group.add(new Line(__assign({ x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, stroke: _this.getValueColor(subValue) }, _this.getLineStyle(_this.option.subtick))));
            }
        });
        return group;
    };
    GaugeModel.prototype.renderTickLabels = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('guage-tick-label'),
        });
        var tickLabel = this.option.tickLabel;
        var inside = tickLabel.inside, margin = tickLabel.margin;
        var _a = this.state, cx = _a.cx, cy = _a.cy, radius = _a.radius, innerRadius = _a.innerRadius, startAngle = _a.startAngle, endAngle = _a.endAngle;
        var r = inside ? innerRadius : radius;
        this._scale.setFormatter(this.option.tickLabel.formatter);
        var polar = getPolar({ cx: cx, cy: cy });
        this.state.ticks.forEach(function (tick, index) {
            if (index === 0 && endAngle - startAngle >= 360) {
                return;
            }
            var angle = _this._scale.scale(tick);
            var label = _this._scale.getLabel(tick);
            var point = polar(angle, r + margin * (inside ? -1 : 1));
            var _a = getCircleLayoutLabelAlign(angle, inside), textAlign = _a.textAlign, textBaseline = _a.textBaseline;
            group.add(createRichText(__assign(__assign({ x: point.x, y: point.y, text: label, fill: '#333333' }, _this.getTextStyle(_this.option.tickLabel)), { textAlign: textAlign,
                textBaseline: textBaseline })));
        });
        return group;
    };
    GaugeModel.prototype.renderDataLabel = function () {
        var _a = this.state, cx = _a.cx, cy = _a.cy, radius = _a.radius;
        var _b = this.option, value = _b.value, _c = _b.label, offsetX = _c.offsetX, offsetY = _c.offsetY;
        var x = cx + getPercentOrNumberValue(offsetX, radius);
        var y = cy + getPercentOrNumberValue(offsetY, radius);
        var text = this.getDataLabel(0);
        return createRichText(__assign({ ref: this._labelRef, text: text,
            x: x,
            y: y, fill: this.getValueColor(value) }, this.getTextStyle(this.option.label)));
    };
    GaugeModel.prototype.renderTitle = function () {
        var _a = this.state, cx = _a.cx, cy = _a.cy, innerRadius = _a.innerRadius;
        var _b = this.option.title, offsetX = _b.offsetX, offsetY = _b.offsetY, text = _b.text;
        var x = cx + getPercentOrNumberValue(offsetX, innerRadius);
        var y = cy + getPercentOrNumberValue(offsetY, innerRadius);
        return createRichText(__assign({ text: text,
            x: x,
            y: y }, this.getTextStyle(this.option.title)));
    };
    GaugeModel.prototype.renderGaugeBackground = function () {
        var _a = this.state, cx = _a.cx, cy = _a.cy, radius = _a.radius;
        return new Circle({
            key: 'gauge-background',
            cx: cx,
            cy: cy,
            radius: radius,
            fill: this.option.background,
            pointerEvents: 'none',
        });
    };
    GaugeModel.prototype.render = function () {
        var group = new Group({
            key: this.getKey('gauge'),
        });
        group.add(this.renderGaugeBackground());
        group.add(this.renderBandBackground());
        group.add(this.renderBands());
        group.add(this.renderPointer());
        if (this.option.tickLine.show) {
            group.add(this.renderTickLine());
        }
        if (this.option.tickLabel.show) {
            group.add(this.renderTickLabels());
        }
        if (this.option.subtick.show) {
            group.add(this.renderSubticks());
        }
        if (this.option.title.show) {
            group.add(this.renderTitle());
        }
        if (this.option.label.show) {
            group.add(this.renderDataLabel());
        }
        return group;
    };
    GaugeModel.prototype._getPointerRotation = function (angle) {
        return degToRad(angle + 90);
    };
    GaugeModel.prototype._getGradientColor = function () {
        var _a = this.state, cx = _a.cx, cy = _a.cy, bandItems = _a.bandItems;
        var seriesColors = this.chart.getOption().colors;
        var optionsColors = this.option.colors;
        var colors = optionsColors
            ? isString(optionsColors)
                ? optionsColors.split('-')
                : optionsColors
            : seriesColors;
        if (colors.length < 1 || bandItems.length < 1) {
            return;
        }
        var gradientColor = null;
        var stops = [];
        var roundPI = Math.PI * 2;
        var _b = this._getGradientAngle(), gradientStartAngle = _b.startAngle, gradientEndAngle = _b.endAngle;
        var endOffset = lodash.clamp((gradientEndAngle - gradientStartAngle) / roundPI, 0, 1);
        // all :start
        stops.push({
            offset: 0,
            color: colors[0],
        });
        if (bandItems.length === 1) {
            // one band: end
            stops.push({
                offset: endOffset,
                color: (colors.length === 1 ? colors[0] : colors[1]),
            });
        }
        else {
            // mutiple bands: middle
            bandItems.slice(1, bandItems.length - 1).forEach(function (bandItem) {
                var middleAngle = (bandItem.endAngle + bandItem.startAngle) / 2;
                stops.push({
                    offset: (degToRad(middleAngle) - gradientStartAngle) / roundPI,
                    color: bandItem.color,
                });
            });
            // mutiple bands: end
            stops.push({
                offset: endOffset,
                color: bandItems[bandItems.length - 1].color,
            });
        }
        gradientColor = new ConicGradient({
            cx: cx,
            cy: cy,
            startAngle: gradientStartAngle,
            global: true,
            stops: stops,
        });
        return gradientColor;
    };
    GaugeModel.prototype._getGradientAngle = function () {
        var _a = this.state, startAngle = _a.startAngle, endAngle = _a.endAngle, value = _a.value, radius = _a.radius, innerRadius = _a.innerRadius;
        var _b = this.option, bandStyle = _b.bandStyle, max = _b.max, min = _b.min, useProgressMode = _b.useProgressMode;
        var r = (radius - innerRadius) / 2;
        var R = r + innerRadius;
        var isNeedCalcRound = bandStyle.round;
        var extraAngle = 0;
        if (endAngle - startAngle === 360 && value >= max) {
            isNeedCalcRound = false;
            if (useProgressMode) {
                extraAngle = ((endAngle - startAngle) * (value - max)) / (max - min);
            }
        }
        return {
            startAngle: isNeedCalcRound
                ? degToRad(startAngle) - Math.asin(r / R)
                : degToRad(startAngle + extraAngle),
            endAngle: isNeedCalcRound
                ? degToRad(endAngle) + Math.asin(r / R)
                : degToRad(endAngle + extraAngle),
        };
    };
    return GaugeModel;
}(SeriesModel));
export default GaugeModel;
registerChart('gauge', GaugeModel);
